// src/unAuthPages/HomePage.js — the signed-out home page
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight } from '@fortawesome/free-solid-svg-icons';

import ChannelMarquee from './site/ChannelMarquee';
import CtaBand from './site/CtaBand';
import Faq from './site/Faq';
import GoogleButton from './site/GoogleButton';
import Seo, { ORGANIZATION, SITE_URL } from './site/Seo';
import { APP_PRODUCTS as PRODUCTS } from './site/siteLinks';
import ApiConsole from './developers/ApiConsole';
import './developers/developers.css';
import { USE_CASE_PAGES } from './useCases/useCaseData';
import './useCases/useCases.css';
import UseCases from './home/UseCases';
import PodcastShowcase from './home/PodcastShowcase';
import demo_poster from '../assets/images/default/home-demo-poster.jpg';
import './home/home.css';

// A real chat in a workspace: question, streamed answer, follow-up. Served by Hosting from public/media
// (browsers cache it for an hour, so a re-shot video gets a new file name).
const DEMO_VIDEO = '/media/home-demo.mp4';

// The kinds of video people bring: the first one is what search engines and link previews see
const VIDEO_KINDS = [
  'video', 'lecture', 'meeting', 'interview', 'game film', 'podcast', 'lesson', 'webinar', 'training', 'YouTube video',
];

function HomePage() {
  const [outputIndex, setOutputIndex] = useState(0);

  // Keep the Google Ads click id for sign-up attribution (only when the visit carries one)
  useEffect(() => {
    const gclid = new URLSearchParams(window.location.search).get('gclid');
    if (gclid) localStorage.setItem('gclid', gclid);
  }, []);

  useEffect(() => {
    const timer = setInterval(() => setOutputIndex(i => (i + 1) % VIDEO_KINDS.length), 2800);
    return () => clearInterval(timer);
  }, []);

  return (
    <>
      <Seo
        title="Bumpups | AI chapters, notes and answers for any video"
        description="Upload a lecture, meeting, interview or game film, or paste a YouTube link. Bumpups writes timestamped chapters and answers your questions about the video. Free to start."
        jsonLd={[ORGANIZATION, { '@type': 'WebSite', name: 'Bumpups', url: SITE_URL }]}
      />

      <section className="home-hero mk-hero-bg">
        <div className="mk-container home-hero-text">
          <Link to="/developers" className="home-pill">
            <span className="home-pill-badge">New</span>
            The Bumpups API for Pro
            <FontAwesomeIcon icon={faArrowRight} className="mk-btn-arrow" />
          </Link>
          <h1 className="mk-h1 home-title">
            Chapters, notes and answers{' '}
            <span className="home-title-line">
              from any{' '}
              <span className="home-rotator" aria-live="off">
                <span key={outputIndex} className="home-rotator-word">{VIDEO_KINDS[outputIndex]}</span>
              </span>
            </span>
          </h1>
          <p className="mk-lead home-lead">
            Upload a recording or paste a YouTube link. Bump AI watches the whole thing, writes timestamped
            chapters, and answers your questions, so you can find any moment in seconds.
          </p>
          <div className="mk-actions mk-actions--center">
            <Link to="/signup" className="mk-btn mk-btn--primary">
              Start free with email <FontAwesomeIcon icon={faArrowRight} className="mk-btn-arrow" />
            </Link>
            <GoogleButton />
          </div>
          <p className="mk-note">60 free minutes every month · First upload free to preview · No credit card needed</p>
        </div>

        <div className="mk-container home-demo">
          <div className="mk-glow">
            <div className="mk-frame home-demo-frame">
              <video src={DEMO_VIDEO} poster={demo_poster} autoPlay muted loop playsInline aria-label="Demo: asking Bump AI about a Harvard lecture in a workspace, then a follow-up" />
            </div>
          </div>
        </div>
      </section>

      <div className="home-proof">
        <ChannelMarquee caption="Lectures, lessons, meetings, interviews, game film and YouTube videos from these channels and more." />
      </div>

      <section className="mk-section">
        <div className="mk-container">
          <div className="mk-section-head">
            <p className="mk-eyebrow">Use cases</p>
            <h2 className="mk-h2">Built for the videos you need to get through</h2>
            <p className="mk-lead">See what Bumpups makes from each kind of video, on a real example.</p>
          </div>
          <div className="mk-grid mk-grid--3">
            {USE_CASE_PAGES.map(p => (
              <Link key={p.slug} to={`/use-cases/${p.slug}`} className="mk-card uc-card">
                <span className="mk-icon"><FontAwesomeIcon icon={p.icon} /></span>
                <h3 className="mk-h3">{p.label}</h3>
                <p className="mk-text">{p.summary}</p>
              </Link>
            ))}
          </div>
          <p className="mk-note" style={{ textAlign: 'center', marginTop: 24 }}>
            Working with YouTube? Try the free <Link to="/tools/youtube-timestamp-generator" className="mk-accent">timestamp generator</Link>,{' '}
            <Link to="/tools/youtube-transcript" className="mk-accent">transcript tool</Link> and{' '}
            <Link to="/tools/youtube-chapter-checker" className="mk-accent">chapter checker</Link>.
          </p>
        </div>
      </section>

      <UseCases />

      <section className="mk-section">
        <div className="mk-container">
          <div className="mk-section-head">
            <p className="mk-eyebrow">Product</p>
            <h2 className="mk-h2">One home for every video</h2>
            <p className="mk-lead">Bring in YouTube links or your own files, then chat, summarize, and publish from one place.</p>
          </div>
          <div className="mk-grid mk-grid--4">
            {PRODUCTS.map(p => (
              <Link key={p.to} to={p.to} className="mk-card home-product">
                <span className="mk-icon"><FontAwesomeIcon icon={p.icon} /></span>
                <h3 className="mk-h3">{p.title}</h3>
                <p className="mk-text">{p.summary}</p>
                <span className="home-product-more">
                  Learn more <FontAwesomeIcon icon={faArrowRight} className="mk-btn-arrow" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <PodcastShowcase />

      <section className="mk-section">
        <div className="mk-container home-api">
          <div>
            <p className="mk-eyebrow">Bumpups API</p>
            <h2 className="mk-h2">Chapters from your own code</h2>
            <p className="mk-lead">
              Send a YouTube link to the API and get timestamped chapters back, then ask questions about the video.
              It comes with Pro and uses the same minutes as the app.
            </p>
            <div className="mk-actions">
              <Link to="/developers" className="mk-btn mk-btn--primary">
                Explore the API <FontAwesomeIcon icon={faArrowRight} className="mk-btn-arrow" />
              </Link>
              <Link to="/developers/docs" className="mk-btn">Read the docs</Link>
            </div>
          </div>
          <ApiConsole chapters={5} />
        </div>
      </section>

      <Faq />
      <CtaBand />
    </>
  );
}

export default HomePage;
